import Link from "@components/elements/Link"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import { trackSegmentEvent } from "@utils/analytics"
import { storyblokLinkToYocoLink } from "@utils/storyblok"
import { getTextColorClass } from "@utils/V2/color"
import React, { useCallback } from "react"

type Props = {
  label: string
  description: string
  descriptionMobile?: string
  ctaLink?: Storyblok.Link
  image?: string
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
  onClick?: () => void
}

export const GetStartedModalItem = ({
  label,
  description,
  descriptionMobile,
  ctaLink,
  image,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  onClick,
}: Props) => {
  const hoverClass = `group-hover:${getTextColorClass(Color.White)}`
  const wrapperClass =
    "text-left group flex h-full w-full items-center gap-20 border-t border-light-gray px-[25px] py-24 hover:bg-blue"

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick()
    }

    trackSegmentEvent("get_started_modal_click", {
      anchorText: label,
      path: storyblokLinkToYocoLink(ctaLink),
    })

    if (trackingEvent) {
      const event =
        trackingEventKey && trackingEventValue
          ? { [trackingEventKey]: trackingEventValue }
          : undefined

      trackSegmentEvent(trackingEvent, event)
    }
  }, [
    label,
    ctaLink,
    onClick,
    trackingEvent,
    trackingEventKey,
    trackingEventValue,
  ])

  const content = (
    <>
      {image && (
        <GatsbyStoryblokImage
          image={image}
          alt={label}
          width={200}
          height={200}
          className="max-h-[64px] max-w-[64px] rounded-[4px]"
        />
      )}

      <div className="w-full">
        <div className="flex items-center justify-between">
          <Typography
            font="grotesk"
            size="body-lg"
            weight="medium"
            text={label}
            color={Color.Black}
            className={hoverClass}
          />

          <ArrowIcon className="hidden sm:inline" />
        </div>

        <Typography
          font="grotesk"
          size="body-sm"
          weight="book"
          text={description}
          color={Color.Charcoal}
          className="hidden sm:inline"
        />

        <Typography
          font="grotesk"
          size="body-sm"
          weight="book"
          text={descriptionMobile || ""}
          color={Color.Charcoal}
          className="sm:hidden"
        />
      </div>
    </>
  )

  if (ctaLink) {
    return (
      <Link
        link={storyblokLinkToYocoLink(ctaLink) ?? ctaLink}
        onClick={handleClick}
        className={wrapperClass}
      >
        {content}
      </Link>
    )
  }

  return (
    <button onClick={handleClick} className={wrapperClass}>
      {content}
    </button>
  )
}
